import type { Doc } from '../../convex/_generated/dataModel';

type Summary = NonNullable<Doc<'meetings'>['summary']>;
type RGB = [number, number, number];
type InlineStyle = 'normal' | 'bold' | 'italic' | 'bolditalic' | 'code';

interface InlineToken {
  text: string;
  style: InlineStyle;
}

interface Segment {
  text: string;
  style: InlineStyle;
  width: number;
  isSpace: boolean;
}

interface LaidLine {
  segments: Segment[];
}

const COLORS = {
  ink: [20, 28, 45] as RGB,
  inkSoft: [56, 66, 88] as RGB,
  muted: [104, 117, 138] as RGB,
  faint: [168, 180, 198] as RGB,
  line: [218, 210, 195] as RGB,
  lineSoft: [232, 226, 214] as RGB,
  panel: [250, 248, 243] as RGB,
  panelAlt: [243, 238, 228] as RGB,
  panelDeep: [236, 230, 218] as RGB,
  gold: [151, 111, 42] as RGB,
  goldSoft: [212, 184, 130] as RGB,
  danger: [142, 50, 50] as RGB,
  dangerSoft: [222, 196, 196] as RGB,
};

const PAGE = {
  marginX: 56,
  marginTop: 64,
  marginBottom: 68,
};

const BODY = {
  fontSize: 11,
  lineHeight: 17,
};

const TRACKING = {
  eyebrow: 1.2,
  section: 1.2,
};

export async function downloadSummaryPdf(summary: Summary, audioFileName: string): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - PAGE.marginX * 2;
  const pageBottom = pageHeight - PAGE.marginBottom;
  const usableHeight = pageBottom - PAGE.marginTop;
  const generatedAt = new Intl.DateTimeFormat('es-CO', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date());
  const baseName = fileBaseName(audioFileName);
  let y = PAGE.marginTop;
  let sectionIndex = 0;

  doc.setProperties({
    title: summary.title,
    subject: 'Resumen de reunion',
    creator: 'Meeting Transcriber',
  });

  drawDocumentHeader();
  if (summary.overview) {
    drawSectionTitle('Resumen ejecutivo');
    drawParagraph(summary.overview, { color: COLORS.inkSoft });
  }
  if (summary.participants.length) {
    drawSectionTitle('Participantes', summary.participants.length);
    drawParticipants(summary.participants);
  }
  if (summary.keyPoints.length) {
    drawSectionTitle('Puntos clave', summary.keyPoints.length);
    drawNumberedList(summary.keyPoints);
  }
  if (summary.decisions.length) {
    drawSectionTitle('Decisiones', summary.decisions.length);
    drawBulletList(summary.decisions, 'gold');
  }
  if (summary.actionItems.length) {
    drawSectionTitle('Action items', summary.actionItems.length);
    drawActionItems(summary.actionItems);
  }
  if (summary.risks.length) {
    drawSectionTitle('Riesgos y pendientes', summary.risks.length);
    drawBulletList(summary.risks, 'danger');
  }
  if (summary.nextSteps.length) {
    drawSectionTitle('Siguientes pasos', summary.nextSteps.length);
    drawNumberedList(summary.nextSteps);
  }

  addFooters();
  doc.save(`${baseName}-resumen.pdf`);

  // ---------- Layout primitives ----------

  function measureTracked(text: string, charSpace: number): number {
    return doc.getTextWidth(text) + Math.max(0, text.length - 1) * charSpace;
  }

  function applyStyle(style: InlineStyle, fontSize: number, color: RGB): void {
    if (style === 'code') {
      setText(COLORS.gold);
      doc.setFont('courier', 'normal');
      doc.setFontSize(fontSize - 0.5);
    } else {
      setText(color);
      const weight =
        style === 'bolditalic' ? 'bolditalic' : style === 'bold' ? 'bold' : style === 'italic' ? 'italic' : 'normal';
      doc.setFont('helvetica', weight);
      doc.setFontSize(fontSize);
    }
  }

  function measureSegments(text: string, fontSize: number): Segment[] {
    const tokens = tokenizeInlineMarkdown(text.replace(/\s+/g, ' ').trim());
    const segments: Segment[] = [];
    for (const token of tokens) {
      const parts = token.text.split(/(\s+)/).filter((p) => p.length > 0);
      for (const part of parts) {
        const isSpace = /^\s+$/.test(part);
        const value = isSpace ? ' ' : part;
        applyStyle(token.style, fontSize, COLORS.ink);
        const width = doc.getTextWidth(value);
        segments.push({ text: value, style: token.style, width, isSpace });
      }
    }
    return segments;
  }

  function layoutLines(segments: Segment[], maxWidth: number, fontSize: number): LaidLine[] {
    const lines: LaidLine[] = [];
    let current: Segment[] = [];
    let cursor = 0;
    for (const seg of segments) {
      if (seg.isSpace) {
        if (current.length === 0) continue;
        current.push(seg);
        cursor += seg.width;
        continue;
      }
      if (current.length > 0 && cursor + seg.width > maxWidth) {
        while (current.length && current[current.length - 1].isSpace) {
          const removed = current.pop()!;
          cursor -= removed.width;
        }
        lines.push({ segments: current });
        current = [];
        cursor = 0;
      }
      if (current.length === 0 && seg.width > maxWidth) {
        const fragments = breakLongWord(seg, maxWidth, fontSize);
        for (let i = 0; i < fragments.length; i++) {
          if (i < fragments.length - 1) {
            lines.push({ segments: [fragments[i]] });
          } else {
            current.push(fragments[i]);
            cursor = fragments[i].width;
          }
        }
        continue;
      }
      current.push(seg);
      cursor += seg.width;
    }
    if (current.length) {
      while (current.length && current[current.length - 1].isSpace) current.pop();
      lines.push({ segments: current });
    }
    return lines.length ? lines : [{ segments: [] }];
  }

  function breakLongWord(seg: Segment, maxWidth: number, fontSize: number): Segment[] {
    applyStyle(seg.style, fontSize, COLORS.ink);
    const fragments: Segment[] = [];
    let buffer = '';
    let bufferWidth = 0;
    for (const ch of seg.text) {
      const charWidth = doc.getTextWidth(ch);
      if (bufferWidth + charWidth > maxWidth && buffer.length > 0) {
        fragments.push({ text: buffer, style: seg.style, width: bufferWidth, isSpace: false });
        buffer = ch;
        bufferWidth = charWidth;
      } else {
        buffer += ch;
        bufferWidth += charWidth;
      }
    }
    if (buffer) fragments.push({ text: buffer, style: seg.style, width: bufferWidth, isSpace: false });
    return fragments;
  }

  function drawLines(
    lines: LaidLine[],
    x: number,
    startY: number,
    options: { fontSize: number; lineHeight: number; color: RGB }
  ): number {
    let cursorY = startY;
    for (let i = 0; i < lines.length; i++) {
      let cursorX = x;
      for (const seg of lines[i].segments) {
        applyStyle(seg.style, options.fontSize, options.color);
        if (!seg.isSpace || cursorX > x) {
          doc.text(seg.text, cursorX, cursorY);
        }
        cursorX += seg.width;
      }
      if (i < lines.length - 1) cursorY += options.lineHeight;
    }
    return cursorY;
  }

  function ensure(height: number): void {
    if (y + height <= pageBottom) return;
    doc.addPage();
    y = PAGE.marginTop;
  }

  function ensureBlock(height: number): void {
    if (y + height <= pageBottom) return;
    if (height <= usableHeight) {
      doc.addPage();
      y = PAGE.marginTop;
      return;
    }
    doc.addPage();
    y = PAGE.marginTop;
  }

  /** Dibuja líneas de texto con salto de página; repite el marcador solo al continuar en una página nueva. */
  function drawFlowingLines(
    lines: LaidLine[],
    textX: number,
    options: { fontSize: number; lineHeight: number; color: RGB },
    onContinuation?: () => void
  ): void {
    const { fontSize, lineHeight, color } = options;

    for (let i = 0; i < lines.length; i++) {
      if (i > 0 && y + lineHeight > pageBottom) {
        doc.addPage();
        y = PAGE.marginTop;
        onContinuation?.();
      }
      drawLines([lines[i]], textX, y, { fontSize, lineHeight, color });
      y += lineHeight;
    }
  }

  // ---------- Sections ----------

  function drawDocumentHeader(): void {
    const pad = 20;
    const innerWidth = contentWidth - pad * 2;
    const titleFontSize = 28;
    const titleLineHeight = 32;

    const titleSegments = measureSegments(summary.title, titleFontSize);
    const titleLines = layoutLines(titleSegments, innerWidth, titleFontSize);
    const titleBlockHeight = titleLines.length * titleLineHeight;

    const metaRows = 2;
    const metaRowHeight = 14;
    const metaBlockHeight = metaRows * metaRowHeight + 8;
    const boxHeight = 48 + titleBlockHeight + 16 + metaBlockHeight + pad;

    ensureBlock(boxHeight + 28);

    const boxX = PAGE.marginX;
    const boxY = y;

    setFill(COLORS.panel);
    doc.roundedRect(boxX, boxY, contentWidth, boxHeight, 12, 12, 'F');
    setStroke(COLORS.line);
    doc.setLineWidth(0.5);
    doc.roundedRect(boxX, boxY, contentWidth, boxHeight, 12, 12, 'S');

    setFill(COLORS.gold);
    doc.rect(boxX, boxY, 2, boxHeight, 'F');

    setFill(COLORS.goldSoft);
    for (let i = 0; i < 3; i++) {
      doc.circle(boxX + contentWidth - pad - i * 10, boxY + pad + 4, 1.5, 'F');
    }

    const contentX = boxX + pad + 6;

    setText(COLORS.muted);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    const eyebrow = `RESUMEN DE REUNIÓN · ${generatedAt.toUpperCase()}`;
    doc.text(eyebrow, contentX, boxY + pad + 10, { charSpace: TRACKING.eyebrow });

    const titleY = boxY + pad + 36;
    drawLines(titleLines, contentX, titleY, {
      fontSize: titleFontSize,
      lineHeight: titleLineHeight,
      color: COLORS.ink,
    });

    const dividerY = boxY + pad + 36 + titleBlockHeight + 10;
    setStroke(COLORS.lineSoft);
    doc.setLineWidth(0.4);
    doc.line(contentX, dividerY, boxX + contentWidth - pad, dividerY);

    setText(COLORS.muted);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    const metaY = dividerY + 18;
    const colMid = contentX + innerWidth * 0.42;

    doc.setFont('helvetica', 'bold');
    doc.text('ARCHIVO', contentX, metaY);
    doc.setFont('helvetica', 'normal');
    doc.text(truncate(audioFileName, 48), contentX, metaY + metaRowHeight);

    doc.setFont('helvetica', 'bold');
    doc.text('GENERADO', colMid, metaY);
    doc.setFont('helvetica', 'normal');
    doc.text(generatedAt, colMid, metaY + metaRowHeight);

    y = boxY + boxHeight + 28;
  }

  function drawSectionTitle(label: string, count?: number): void {
    sectionIndex += 1;
    const advance = y > PAGE.marginTop + 4 ? 22 : 0;
    const titleBlockHeight = 28;
    ensure(advance + titleBlockHeight + BODY.lineHeight * 2);
    y += advance;

    const numText = String(sectionIndex).padStart(2, '0');
    const labelUpper = label.toUpperCase();

    setText(COLORS.gold);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(numText, PAGE.marginX, y);

    const numWidth = doc.getTextWidth(numText);
    const sepX = PAGE.marginX + numWidth + 8;
    setText(COLORS.faint);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('·', sepX, y);

    const labelX = sepX + doc.getTextWidth('·') + 8;
    setText(COLORS.ink);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(labelUpper, labelX, y, { charSpace: TRACKING.section });

    let rightEdge = labelX + measureTracked(labelUpper, TRACKING.section);
    if (count !== undefined) {
      const countText = String(count).padStart(2, '0');
      const countX = rightEdge + 12;
      setText(COLORS.gold);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text(countText, countX, y);
      rightEdge = countX + doc.getTextWidth(countText);
    }

    const ruleY = y + 6;
    setStroke(COLORS.lineSoft);
    doc.setLineWidth(0.5);
    doc.line(PAGE.marginX, ruleY, pageWidth - PAGE.marginX, ruleY);

    y += titleBlockHeight;
  }

  function drawParagraph(
    text: string,
    options: { fontSize?: number; lineHeight?: number; color?: RGB } = {}
  ): void {
    const fontSize = options.fontSize ?? BODY.fontSize;
    const lineHeight = options.lineHeight ?? BODY.lineHeight;
    const color = options.color ?? COLORS.inkSoft;
    const lines = layoutLines(measureSegments(text, fontSize), contentWidth, fontSize);

    for (let i = 0; i < lines.length; i++) {
      ensure(lineHeight);
      drawLines([lines[i]], PAGE.marginX, y, { fontSize, lineHeight, color });
      y += lineHeight;
    }
    y += 12;
  }

  function drawParticipants(participants: string[]): void {
    const fontSize = 9.5;
    const chipHeight = 24;
    const gap = 8;
    const rowGap = 10;
    const padX = 12;

    let x = PAGE.marginX;
    let rowY = y;

    ensure(chipHeight + 4);
    rowY = y;

    for (const participant of participants) {
      const label = stripMarkdown(participant);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fontSize);
      const rawWidth = doc.getTextWidth(label);
      const maxChipWidth = contentWidth;
      const chipWidth = Math.min(rawWidth + padX * 2, maxChipWidth);
      const fitsLine = x + chipWidth <= pageWidth - PAGE.marginX;

      if (!fitsLine && x !== PAGE.marginX) {
        x = PAGE.marginX;
        rowY += chipHeight + rowGap;
      }
      if (rowY + chipHeight > pageBottom) {
        doc.addPage();
        x = PAGE.marginX;
        rowY = PAGE.marginTop;
      }

      setFill(COLORS.panelAlt);
      setStroke(COLORS.line);
      doc.setLineWidth(0.5);
      doc.roundedRect(x, rowY, chipWidth, chipHeight, 12, 12, 'FD');

      setFill(COLORS.gold);
      doc.circle(x + 7, rowY + chipHeight / 2, 1.6, 'F');

      setText(COLORS.ink);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(fontSize);
      const textValue =
        rawWidth + padX * 2 > maxChipWidth
          ? truncateToWidth(label, chipWidth - padX * 2 - 6, fontSize)
          : label;
      doc.text(textValue, x + padX + 6, rowY + chipHeight / 2 + 3);

      x += chipWidth + gap;
    }

    y = rowY + chipHeight + 14;
  }

  function drawNumberBadge(index: number): void {
    const numColWidth = 28;
    const numText = String(index + 1).padStart(2, '0');
    setText(COLORS.gold);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    const numWidth = doc.getTextWidth(numText);
    doc.text(numText, PAGE.marginX + numColWidth - numWidth, y);
  }

  function drawNumberedList(items: string[]): void {
    const numColWidth = 28;
    const textX = PAGE.marginX + numColWidth + 8;
    const textWidth = contentWidth - numColWidth - 8;
    const fontSize = BODY.fontSize;
    const lineHeight = BODY.lineHeight;
    const itemGap = 10;

    items.forEach((item, index) => {
      const lines = layoutLines(measureSegments(item, fontSize), textWidth, fontSize);
      const totalHeight = lines.length * lineHeight + itemGap;
      ensureBlock(Math.min(totalHeight, usableHeight));

      drawNumberBadge(index);
      drawFlowingLines(lines, textX, { fontSize, lineHeight, color: COLORS.ink }, () => {
        drawNumberBadge(index);
      });
      y += itemGap;
    });
  }

  function drawBulletMarker(tone: 'gold' | 'danger'): void {
    const color = tone === 'gold' ? COLORS.gold : COLORS.danger;
    setFill(color);
    doc.circle(PAGE.marginX + 6, y - 3.5, 2.4, 'F');
  }

  function drawBulletList(items: string[], tone: 'gold' | 'danger'): void {
    const indent = 22;
    const textX = PAGE.marginX + indent;
    const textWidth = contentWidth - indent;
    const fontSize = BODY.fontSize;
    const lineHeight = BODY.lineHeight;
    const itemGap = 10;

    items.forEach((item) => {
      const lines = layoutLines(measureSegments(item, fontSize), textWidth, fontSize);
      const totalHeight = lines.length * lineHeight + itemGap;
      ensureBlock(Math.min(totalHeight, usableHeight));

      drawBulletMarker(tone);
      drawFlowingLines(lines, textX, { fontSize, lineHeight, color: COLORS.ink }, () => {
        drawBulletMarker(tone);
      });
      y += itemGap;
    });
  }

  function drawActionItems(items: Summary['actionItems']): void {
    const cardPad = 14;
    const cardGap = 8;
    const ownerFont = 9;
    const ownerLh = 13;
    const taskFont = BODY.fontSize;
    const taskLh = BODY.lineHeight;
    const dateFont = 8.5;
    const headerRowHeight = 18;
    const minCardHeight = cardPad * 2 + headerRowHeight + taskLh;

    items.forEach((item) => {
      const ownerText = item.owner || 'Sin asignar';
      const dateText = item.dueDate ?? 'Sin fecha';
      const ownerLines = layoutLines(
        measureSegments(ownerText, ownerFont),
        contentWidth - cardPad * 2 - 100,
        ownerFont
      );
      const taskLines = layoutLines(
        measureSegments(item.task, taskFont),
        contentWidth - cardPad * 2,
        taskFont
      );
      const taskBlockH = taskLines.length * taskLh;
      const cardHeight = Math.max(minCardHeight, cardPad + headerRowHeight + taskBlockH + cardPad);

      ensureBlock(Math.min(cardHeight + cardGap, usableHeight));

      const cardY = y;

      setFill(COLORS.panel);
      setStroke(COLORS.lineSoft);
      doc.setLineWidth(0.5);
      doc.roundedRect(PAGE.marginX, cardY, contentWidth, cardHeight, 10, 10, 'FD');

      const innerX = PAGE.marginX + cardPad;
      const headerY = cardY + cardPad + ownerLh - 2;

      setFill(COLORS.panelAlt);
      setStroke(COLORS.line);
      doc.setLineWidth(0.4);
      setText(COLORS.gold);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(ownerFont);
      const ownerTextW = ownerLines.reduce(
        (max, line) =>
          Math.max(
            max,
            line.segments.reduce((w, seg) => w + seg.width, 0)
          ),
        0
      );
      const chipW = Math.max(80, Math.min(ownerTextW + 16, contentWidth - cardPad * 2 - 110));
      doc.roundedRect(innerX, cardY + cardPad, chipW, headerRowHeight, 6, 6, 'FD');

      drawLines(ownerLines, innerX + 8, headerY, {
        fontSize: ownerFont,
        lineHeight: ownerLh,
        color: COLORS.gold,
      });

      const dateX = PAGE.marginX + contentWidth - cardPad;
      setFill(COLORS.panelDeep);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(dateFont);
      const dateLabel = dateText;
      const dateW = doc.getTextWidth(dateLabel) + 14;
      const pillX = dateX - dateW;
      doc.roundedRect(pillX, cardY + cardPad + 2, dateW, headerRowHeight - 4, 6, 6, 'F');
      setText(COLORS.muted);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(dateFont);
      doc.text(dateLabel, pillX + 7, headerY);

      const taskY = cardY + cardPad + headerRowHeight + 8 + taskLh - 4;
      drawLines(taskLines, innerX, taskY, {
        fontSize: taskFont,
        lineHeight: taskLh,
        color: COLORS.ink,
      });

      y = cardY + cardHeight + cardGap;
    });

    y += 4;
  }

  // ---------- Footers ----------

  function addFooters(): void {
    const pageCount = doc.getNumberOfPages();
    const brand = 'Meeting Transcriber';

    for (let page = 1; page <= pageCount; page++) {
      doc.setPage(page);
      const footerY = pageHeight - 24;
      const ruleY = pageHeight - 40;

      setStroke(COLORS.lineSoft);
      doc.setLineWidth(0.5);
      doc.line(PAGE.marginX, ruleY, pageWidth - PAGE.marginX, ruleY);

      setText(COLORS.faint);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.text(baseName, PAGE.marginX, footerY);

      setFill(COLORS.gold);
      doc.circle(pageWidth - PAGE.marginX - measureTracked(brand, 0) - 14, footerY - 2.5, 1.2, 'F');
      setText(COLORS.faint);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.text(brand, pageWidth - PAGE.marginX, footerY, { align: 'right' });

      const pageLabel = `${page} / ${pageCount}`;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      const labelWidth = doc.getTextWidth(pageLabel);
      setText(COLORS.muted);
      doc.text(pageLabel, (pageWidth - labelWidth) / 2, footerY);
    }
  }

  // ---------- Drawing helpers ----------

  function setText(color: RGB): void {
    doc.setTextColor(color[0], color[1], color[2]);
  }
  function setFill(color: RGB): void {
    doc.setFillColor(color[0], color[1], color[2]);
  }
  function setStroke(color: RGB): void {
    doc.setDrawColor(color[0], color[1], color[2]);
  }

  function truncateToWidth(text: string, maxWidth: number, fontSize: number): string {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(fontSize);
    if (doc.getTextWidth(text) <= maxWidth) return text;
    const ellipsis = '...';
    let lo = 0;
    let hi = text.length;
    while (lo < hi) {
      const mid = Math.floor((lo + hi + 1) / 2);
      if (doc.getTextWidth(text.slice(0, mid) + ellipsis) <= maxWidth) lo = mid;
      else hi = mid - 1;
    }
    return text.slice(0, lo) + ellipsis;
  }
}

// ---------- Pure helpers ----------

function tokenizeInlineMarkdown(text: string): InlineToken[] {
  const tokens: InlineToken[] = [];
  const pattern = /(`[^`]+`|\*\*[^*]+\*\*|__[^_]+__|\*[^*]+\*|_[^_]+_)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({ text: text.slice(lastIndex, match.index), style: 'normal' });
    }
    tokens.push(tokenFromMarkdown(match[0]));
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    tokens.push({ text: text.slice(lastIndex), style: 'normal' });
  }
  return tokens.filter((token) => token.text.length > 0);
}

function tokenFromMarkdown(raw: string): InlineToken {
  if (raw.startsWith('`') && raw.endsWith('`')) {
    return { text: raw.slice(1, -1), style: 'code' };
  }
  if ((raw.startsWith('**') && raw.endsWith('**')) || (raw.startsWith('__') && raw.endsWith('__'))) {
    return { text: raw.slice(2, -2), style: 'bold' };
  }
  if ((raw.startsWith('*') && raw.endsWith('*')) || (raw.startsWith('_') && raw.endsWith('_'))) {
    return { text: raw.slice(1, -1), style: 'italic' };
  }
  return { text: raw, style: 'normal' };
}

function stripMarkdown(text: string): string {
  return text
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max - 1) + '…';
}

function fileBaseName(fileName: string): string {
  const withoutExt = fileName.replace(/\.[^/.]+$/, '') || 'reunion';
  return (
    withoutExt
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9._-]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'reunion'
  );
}
